/**
 * Iframe runtime injected as an inline <script> by the host before srcdoc is set.
 * Implements the host↔iframe contract from
 * `.specify/specs/001-core-design-platform/contracts/postmessage.md`.
 *
 * The body is kept as a string deliberately: it runs in the iframe's window,
 * not the host's, so we don't want Vite to treat it as a regular module.
 * Host-side types live in `src/lib/postmessage.ts`.
 */

export const RUNTIME_VERSION = '1.0.0';
export const PROTOCOL_VERSION = 1;

export const RUNTIME_SOURCE = `(function () {
  'use strict';

  var PROTOCOL_VERSION = 1;
  var RUNTIME_VERSION = '${RUNTIME_VERSION}';
  var TWEAK_STYLE_ID = 'cdc-tweak-overrides';
  var HIGHLIGHT_STYLE_ID = 'cdc-highlight-style';

  var mode = 'select';

  function uid() {
    if (window.crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return 'r-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  function postToHost(type, payload) {
    try {
      parent.postMessage({ v: PROTOCOL_VERSION, type: type, id: uid(), payload: payload }, '*');
    } catch (e) {
      // host gone; ignore
    }
  }

  function safeQuery(selector) {
    try { return document.querySelector(selector); } catch (e) { return null; }
  }

  function applyEdits(edits) {
    if (!edits || !edits.length) return;
    for (var i = 0; i < edits.length; i++) {
      var edit = edits[i];
      var node = safeQuery(edit.selector);
      if (node) node.textContent = edit.newText;
    }
  }

  function applyTweaks(values) {
    if (!values) return;
    var style = document.getElementById(TWEAK_STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = TWEAK_STYLE_ID;
      document.head.appendChild(style);
    }
    var lines = [':root {'];
    for (var i = 0; i < values.length; i++) {
      var v = values[i];
      lines.push('  ' + v.variable + ': ' + v.value + ';');
    }
    lines.push('}');
    style.textContent = lines.join('\\n');
  }

  function setHighlight(selector) {
    var style = document.getElementById(HIGHLIGHT_STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = HIGHLIGHT_STYLE_ID;
      document.head.appendChild(style);
    }
    if (!selector) {
      style.textContent = '';
      return;
    }
    var safe = String(selector).replace(/[\\\\"]/g, '\\\\$&');
    style.textContent = safe + ' { outline: 2px solid #2563eb !important; outline-offset: 2px !important; }';
  }

  function buildSelectorFor(el) {
    if (!el || el === document.documentElement) return null;
    if (el.id) {
      var byId = '#' + el.id;
      if (document.querySelectorAll(byId).length === 1) return byId;
    }
    var role = el.getAttribute && el.getAttribute('role');
    var ariaLabel = el.getAttribute && el.getAttribute('aria-label');
    var tag = el.tagName.toLowerCase();
    if (ariaLabel) {
      var byLabel = tag + '[aria-label="' + ariaLabel.replace(/"/g, '\\\\"') + '"]';
      if (document.querySelectorAll(byLabel).length === 1) return byLabel;
    }
    if (role) {
      var byRole = tag + '[role="' + role + '"]';
      if (document.querySelectorAll(byRole).length === 1) return byRole;
    }
    // structural fallback
    var parts = [];
    var cur = el;
    while (cur && cur.nodeType === 1 && cur !== document.body) {
      var t = cur.tagName.toLowerCase();
      var parent = cur.parentNode;
      if (!parent) break;
      var siblings = parent.children;
      var idx = 1;
      for (var i = 0; i < siblings.length; i++) {
        if (siblings[i] === cur) break;
        if (siblings[i].tagName === cur.tagName) idx++;
      }
      parts.unshift(t + ':nth-of-type(' + idx + ')');
      cur = parent;
    }
    return 'body > ' + parts.join(' > ');
  }

  function snippetFor(el) {
    var html = '';
    try { html = el.outerHTML || ''; } catch (e) { html = ''; }
    if (html.length > 200) html = html.slice(0, 197) + '...';
    return html;
  }

  function rectFor(el) {
    var r = el.getBoundingClientRect();
    return { x: r.left, y: r.top, width: r.width, height: r.height };
  }

  function onClick(e) {
    if (mode !== 'comment') return;
    var t = e.target;
    if (!(t && t.nodeType === 1)) return;
    e.preventDefault();
    e.stopPropagation();
    var selector = buildSelectorFor(t);
    if (!selector) return;
    postToHost('element-clicked', {
      selector: selector,
      snippet: snippetFor(t),
      rect: rectFor(t),
    });
  }

  function onBlur(e) {
    if (mode !== 'edit') return;
    var t = e.target;
    if (!t || !t.isContentEditable) return;
    var selector = buildSelectorFor(t);
    if (!selector) return;
    var newText = (t.textContent || '').trim();
    var oldText = t.dataset && t.dataset.cdcOriginalText != null ? t.dataset.cdcOriginalText : newText;
    if (newText === oldText) return;
    postToHost('text-edited', { selector: selector, oldText: oldText, newText: newText });
  }

  function setEditable(on) {
    var nodes = document.querySelectorAll('h1,h2,h3,h4,h5,h6,p,span,a,button,li,td,th,label');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (on) {
        if (n.dataset.cdcOriginalText == null) n.dataset.cdcOriginalText = n.textContent || '';
        n.setAttribute('contenteditable', 'true');
      } else {
        n.removeAttribute('contenteditable');
      }
    }
  }

  function handle(env) {
    if (!env || env.v !== PROTOCOL_VERSION || typeof env.type !== 'string') return;
    var p = env.payload;
    switch (env.type) {
      case 'apply-edits': applyEdits(p && p.edits); break;
      case 'apply-tweaks': applyTweaks(p && p.values); break;
      case 'highlight': setHighlight(p && p.selector); break;
      case 'enter-mode':
        if (p && (p.mode === 'select' || p.mode === 'comment' || p.mode === 'edit' || p.mode === 'tweaks')) {
          mode = p.mode;
          setEditable(mode === 'edit');
        }
        break;
      case 'patch-append':
        if (p && typeof p.chunk === 'string' && document.body) {
          document.body.insertAdjacentHTML('beforeend', p.chunk);
        }
        break;
      // 'replace' is handled by reloading the iframe srcdoc from the host side.
      default: break;
    }
  }

  window.addEventListener('message', function (e) {
    if (e.source !== window.parent) return;
    handle(e.data);
  });

  document.addEventListener('click', onClick, true);
  document.addEventListener('blur', onBlur, true);

  window.addEventListener('error', function (ev) {
    postToHost('error', { message: ev.message || 'iframe error' });
  });

  function emitReady() {
    postToHost('ready', { runtimeVersion: RUNTIME_VERSION });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', emitReady);
  } else {
    emitReady();
  }
})();`;
